import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Trash2, Plus, Minus, ShoppingBag, Truck, Tag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatEUR, FREE_SHIPPING_THRESHOLD, PROMO_MIN, PROMO_CODE, PROMO_PERCENT, lineTotal, itemDeal } from "@/lib/format";

export default function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, removeItem, updateQty, subtotal, count } = useCart();
  const navigate = useNavigate();
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);
  const promoRemaining = Math.max(0, PROMO_MIN - subtotal);
  const promoProgress = Math.min(100, (subtotal / PROMO_MIN) * 100);

  const goCheckout = () => {
    setDrawerOpen(false);
    navigate("/paiement");
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]"
            onClick={() => setDrawerOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.32, ease: [0.2, 0.7, 0.2, 1] }}
            className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#0d0a12] border-l border-amber-900/30 z-[70] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-5 border-b border-amber-900/30">
              <div className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-amber-500" />
                <h2 className="font-display text-xl font-bold text-stone-100">Votre panier</h2>
                <span className="text-sm text-stone-500 font-mono-tech">({count})</span>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 rounded-lg text-stone-400 hover:text-stone-100 hover:bg-white/5"
                data-testid="cart-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {subtotal > 0 && (
              <div className="px-5 py-4 border-b border-amber-900/20 space-y-3" data-testid="cart-rewards">
                <div>
                  <div className="flex items-center gap-2 text-xs text-stone-300 mb-1.5" data-testid="cart-promo-progress">
                    <Tag className="h-4 w-4 text-fuchsia-400 shrink-0" />
                    {promoRemaining > 0 ? (
                      <span>
                        Plus que <b className="text-fuchsia-300">{formatEUR(promoRemaining)}</b> pour <b>-{PROMO_PERCENT}%</b> (code {PROMO_CODE})
                      </span>
                    ) : (
                      <span className="text-fuchsia-300 font-bold">-{PROMO_PERCENT}% débloqué avec le code {PROMO_CODE} 🎉</span>
                    )}
                  </div>
                  <div className="h-1.5 rounded-full bg-stone-800 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-fuchsia-500 to-purple-600 transition-all duration-500" style={{ width: `${promoProgress}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-xs text-stone-300 mb-1.5">
                    <Truck className="h-4 w-4 text-amber-500 shrink-0" />
                    {remaining > 0 ? (
                      <span>
                        Plus que <b className="text-amber-400">{formatEUR(remaining)}</b> pour la livraison offerte
                      </span>
                    ) : (
                      <span className="text-amber-400 font-bold">Livraison gratuite débloquée 🎉</span>
                    )}
                  </div>
                  <div className="h-1.5 rounded-full bg-stone-800 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-500 to-orange-600 transition-all duration-500" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {items.length === 0 ? (
                <div className="text-center py-20">
                  <ShoppingBag className="h-14 w-14 text-stone-700 mx-auto mb-4" />
                  <p className="text-stone-400 mb-6">Votre panier est vide.</p>
                  <Link
                    to="/boutique"
                    onClick={() => setDrawerOpen(false)}
                    className="btn-ember rounded-xl px-6 py-3 inline-block text-sm"
                  >
                    Découvrir la boutique
                  </Link>
                </div>
              ) : (
                items.map((item) => (
                  <div key={item.key} className="flex gap-3 gothic-card rounded-xl p-3" data-testid={`cart-item-${item.slug}`}>
                    <img src={item.image} alt={item.name} className="h-20 w-20 rounded-lg object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-stone-100 text-sm leading-tight truncate">{item.name}</p>
                      {item.size && <p className="text-xs text-stone-500 mt-0.5">Taille : {item.size}</p>}
                      <p className="font-mono-tech text-amber-400 font-bold mt-1">
                        {formatEUR(lineTotal(item))}
                        {itemDeal(item) && (
                          <span className="ml-2 text-[11px] font-semibold text-green-400">
                            offre {itemDeal(item).label}
                          </span>
                        )}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <button onClick={() => updateQty(item.key, item.quantity - 1)} className="h-7 w-7 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center text-stone-300" data-testid={`qty-minus-${item.slug}`}>
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="font-mono-tech text-sm w-6 text-center">{item.quantity}</span>
                        <button onClick={() => updateQty(item.key, item.quantity + 1)} className="h-7 w-7 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center text-stone-300" data-testid={`qty-plus-${item.slug}`}>
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => removeItem(item.key)} className="ml-auto p-1.5 text-stone-500 hover:text-red-400" data-testid={`remove-${item.slug}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="p-5 border-t border-amber-900/30 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-stone-300">Sous-total</span>
                  <span className="font-mono-tech text-2xl font-extrabold text-amber-400">{formatEUR(subtotal)}</span>
                </div>
                <button onClick={goCheckout} className="btn-ember rounded-xl w-full py-4 text-base" data-testid="cart-checkout-button">
                  Commander
                </button>
                <p className="text-center text-xs text-stone-500">Taxes calculées et code promo appliqués au paiement.</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
